/* eslint-disable jsx-a11y/img-redundant-alt */
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Form,
  Row,
  Col,
  Modal,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faSpinner,
  faEdit,
  faImage,
} from "@fortawesome/free-solid-svg-icons";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import BreadCrumbs from "../../../../components/BreadCrumbs";
import ControlledRowTextField from "../../../../components/ControlledRowTextField";
import ControlledSelect from "../../../../components/ControlledSelect";
import LoadingScreen from "../../../../components/LoadingScreen";
import ButtonComponent from "../../../../components/ButtonComponent";
import EditPhotoUpload from "../../../../components/EditPhotoUpload";
import { uploadImage } from "../../../../components/imageUploadCompont";
import {
  useSaveDimensionValueMutation,
  useGetDimensionsQuery,
  useLazyGetMenuTypesQuery,
  useUpdateDimensionValueImageMutation,
  useUploadSetDimensionLogoFileExtntionHttpsMutation,
  ProductDimension,
  DimensionValue,
} from "../../../../redux/services/CustomTypeService";
import { Label } from "reactstrap";

interface EditDimensionValueFormData {
  displayName: string;
  description: string;
  displayOrder: { value: number; label: string };
}

const EditDimensionValue: React.FC = () => {
  const { menuType, dimensionId, valueId } = useParams<{
    menuType: string;
    dimensionId: string;
    valueId: string;
  }>();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [dimensionValue, setDimensionValue] = useState<DimensionValue | null>(
    null
  );
  const [dimension, setDimension] = useState<ProductDimension | null>(null);
  const [menuTypeId, setMenuTypeId] = useState<string | null>(null);
  const [showImageUpdateModal, setShowImageUpdateModal] = useState(false);
  const [uploadedImgDetails, setUploadedImgDetails] = useState<{
    imgSrc: string;
    formData: any;
    fileExtension: string;
  }>({ imgSrc: "", formData: "", fileExtension: "" });
  const [isImageUpdateLoading, setIsImageUpdateLoading] = useState(false);

  // Get restaurant ID from Redux
  const selectedRestaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id
  );

  // Get menu types to find the ID
  const [triggerMenuTypes, { data: menuTypes = [] }] =
    useLazyGetMenuTypesQuery();

  // Get dimensions data
  const { data: productDimensions = [] } = useGetDimensionsQuery(
    {
      restaurantId: selectedRestaurantId,
      customMenuItemTypeId: menuTypeId || "",
    },
    {
      skip: !selectedRestaurantId || !menuTypeId,
    }
  );

  // Get the mutation hooks
  const [saveDimensionValueMutation] = useSaveDimensionValueMutation();
  const [getDimensionValueImageReference] =
    useUploadSetDimensionLogoFileExtntionHttpsMutation();
  const [updateDimensionValueImage] = useUpdateDimensionValueImageMutation();

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<EditDimensionValueFormData>();

  // Options for display order
  const displayOrderOptions = Array.from({ length: 20 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}`,
  }));

  // Find the menu type ID based on the name from URL
  useEffect(() => {
    if (selectedRestaurantId && menuType) {
      triggerMenuTypes({ restaurantId: selectedRestaurantId });
    }
  }, [selectedRestaurantId, menuType, triggerMenuTypes]);

  useEffect(() => {
    if (menuTypes && menuTypes.length > 0 && menuType) {
      const foundMenuType = menuTypes.find((mt) => mt.name === menuType);
      if (foundMenuType) {
        setMenuTypeId(foundMenuType.id);
      }
    }
  }, [menuTypes, menuType]);

  // Find the specific dimension and dimension value
  useEffect(() => {
    if (
      productDimensions &&
      productDimensions.length > 0 &&
      dimensionId &&
      valueId
    ) {
      const foundDimension = productDimensions.find(
        (dim) => dim.id === dimensionId
      );
      if (foundDimension) {
        setDimension(foundDimension);

        // Find the specific dimension value
        const foundValue = foundDimension.dimensionValue?.find(
          (val) => val.id === valueId
        );
        if (foundValue) {
          setDimensionValue(foundValue);

          // Pre-populate form with existing data
          reset({
            displayName: foundValue.displayName,
            description: foundValue.description,
            displayOrder: {
              value: foundValue.displayOrder,
              label: foundValue.displayOrder.toString(),
            },
          });
        }
      }
      setIsLoading(false);
    }
  }, [productDimensions, dimensionId, valueId, reset]);

  const onSubmit = async (data: EditDimensionValueFormData) => {
    setIsSubmitting(true);

    try {
      if (!selectedRestaurantId) {
        toast.error("Restaurant not selected. Please refresh and try again.");
        return;
      }

      if (!dimensionId || !valueId) {
        toast.error(
          "Dimension or value ID not found. Please navigate back and try again."
        );
        return;
      }

      const dimensionValueData = {
        restaurantId: selectedRestaurantId,
        dimensionId: dimensionId,
        displayName: data.displayName,
        description: data.description,
        displayOrder: data.displayOrder.value,
        dimensionValueId: valueId,
      };

      await saveDimensionValueMutation(dimensionValueData).unwrap();

      toast.success("Dimension value updated successfully!");
      navigate(`/productCatalogue/product-groups/${menuType}/dimensions`, {
        state: { shouldRefetch: true },
      });
    } catch (error: any) {
      console.error("Error updating dimension value:", error);
      const errorMessage =
        error?.data ||
        error?.message ||
        "Failed to update dimension value. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Image update functions
  const handleUpdatePhoto = () => {
    setShowImageUpdateModal(true);
  };

  const handleCloseModal = () => {
    setShowImageUpdateModal(false);
  };

  const imageUploadCallback = (e: any) => {
    setUploadedImgDetails({
      ...uploadedImgDetails,
      imgSrc: e.imgSrc,
      formData: e.formData,
      fileExtension: e.fileExtension,
    });
  };

  const handleDimensionValueImageUpdate = async () => {
    setIsImageUpdateLoading(true);

    try {
      const imageRef = await uploadImage(getDimensionValueImageReference, {
        fileExtention: uploadedImgDetails.fileExtension,
        formData: uploadedImgDetails.formData,
      });

      if (!imageRef) {
        throw new Error("Failed to upload image");
      }

      const updateImageResponse = await updateDimensionValueImage({
        imgRef: imageRef,
        restaurantId: selectedRestaurantId,
        dimensionValueId: valueId!,
      }).unwrap();

      if (updateImageResponse) {
        toast.success("Dimension value image successfully updated");
        navigate(`/productCatalogue/product-groups/${menuType}/dimensions`, {
          state: { shouldRefetch: true },
        });
        if (dimensionValue) {
          setDimensionValue({
            ...dimensionValue,
            imageUrl: imageRef, 
          });
        }
        handleCloseModal();
      }
    } catch (error: any) {
      console.error("Error updating dimension value image:", error);
      toast.error("Failed to update image. Please try again.");
    } finally {
      setIsImageUpdateLoading(false);
    }
  };

  const displayNameWatcher = watch("displayName");
  const descriptionWatcher = watch("description");

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!dimensionValue || !dimension) {
    return (
      <>
        <BreadCrumbs 
          hasSubMenus={true}
          customNameMap={{
            [menuTypeId || '']: menuType || 'Menu Type'
          }}
          disabledCrumbs={['edit-value']}
        />
        <Card className="mt-3">
          <CardBody className="text-center py-5">
            <p className="text-danger">Dimension value not found</p>
            <Button
              variant="primary"
              onClick={() =>
                navigate(
                  `/productCatalogue/product-groups/${menuType}/dimensions`
                )
              }
            >
              Back to Dimensions
            </Button>
          </CardBody>
        </Card>
      </>
    );
  }

  return (
    <>
      <BreadCrumbs 
        hasSubMenus={true}
        customNameMap={{
          [menuTypeId || '']: menuType || 'Menu Type',
          [dimensionId || '']: dimension?.displayName || 'Dimension',
          [valueId || '']: dimensionValue?.displayName || 'Value'
        }}
        disabledCrumbs={['edit-value']}
      />

      <Card className="mt-3">
        <CardHeader className="header-bg-color d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <div>
              <div className="d-flex align-items-center gap-2">
                <h5 className="mb-0">
                  Edit Value: "{dimensionValue?.displayName || "Loading..."}"
                </h5>
              </div>
              <small className="text-light opacity-75">
                {menuType} → {dimension?.displayName || "Loading..."} →{" "}
                {dimensionValue?.displayName || "Loading..."}
              </small>
            </div>
          </div>
          <FontAwesomeIcon icon={faEdit} size="lg" />
        </CardHeader>

        <CardBody>
          {/* Image Display Section with Edit Button - Similar to Set Menu component */}
          <Row className="mb-4">
            <Col lg="4">
              <Label className="form-label">Value Image</Label>
            </Col>
            <Col lg="8">
              <div className="d-flex align-items-center">
                {dimensionValue?.imageUrl ? (
                  <>
                    <img
                      src={dimensionValue.imageUrl}
                      alt={`${dimensionValue.displayName} image`}
                      style={{
                        width: "100px",
                        height: "100px",
                        borderRadius: "10px",
                        objectFit: "cover",
                        marginRight: "15px",
                        border: "1px solid #dee2e6",
                      }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = "none";
                      }}
                    />
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={handleUpdatePhoto}
                      style={{ position: "relative", top: "80%", right: "3%" }}
                      title="Edit Photo"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </Button>
                  </>
                ) : (
                  <div
                    className="image-dimension"
                    style={{
                      width: "100px",
                      height: "100px",
                      borderRadius: "10px",
                      backgroundColor: "#f8f9fa",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: "15px",
                      border: "1px dashed #dee2e6",
                    }}
                  >
                    <div className="text-center">
                      <FontAwesomeIcon
                        icon={faImage}
                        size="lg"
                        className="text-muted mb-2"
                      />
                      <div className="text-muted small">No Image</div>
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleUpdatePhoto}
                      style={{ position: "relative", top: "45%", right: "10%" }}
                      title="Add Photo"
                    >
                      <FontAwesomeIcon icon={faImage} />
                    </Button>
                  </div>
                )}
              </div>
            </Col>
          </Row>

          {/* Image Update Modal */}
          <Modal show={showImageUpdateModal} onHide={handleCloseModal} centered>
            <Modal.Header closeButton>
              <Modal.Title>Update Dimension Value Image</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <EditPhotoUpload
                imageUrl={uploadedImgDetails.imgSrc}
                onUpload={imageUploadCallback}
              />
            </Modal.Body>
            <Modal.Footer>
              {isImageUpdateLoading ? (
                <ButtonComponent
                  titel="Saving..."
                  type="button"
                  icon={<FontAwesomeIcon icon={faSpinner} spin />}
                  isDisabled={isImageUpdateLoading}
                  onClick={handleDimensionValueImageUpdate}
                />
              ) : (
                <ButtonComponent
                  titel="Save"
                  type="button"
                  isDisabled={isImageUpdateLoading}
                  onClick={handleDimensionValueImageUpdate}
                />
              )}
            </Modal.Footer>
          </Modal>

          <hr />

          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row>
              <Col md={6}>
                <ControlledRowTextField
                  label="Display Name"
                  inputName="displayName"
                  inputType="text"
                  placeholder="e.g., Chicken Thigh, Chicken Breast"
                  control={control}
                  rules={{
                    required: "Display name is required",
                    minLength: {
                      value: 2,
                      message: "Display name must be at least 2 characters",
                    },
                    maxLength: {
                      value: 100,
                      message: "Display name must be less than 100 characters",
                    },
                  }}
                />
                {errors.displayName && (
                  <span className="text-danger small">
                    {errors.displayName.message}
                  </span>
                )}
              </Col>

              <Col md={6}>
                <ControlledSelect
                  label="Display Order"
                  inputName="displayOrder"
                  control={control}
                  optionsList={displayOrderOptions}
                  rules={{ required: "Display order is required" }}
                />
                {errors.displayOrder && (
                  <span className="text-danger small">
                    {errors.displayOrder.message}
                  </span>
                )}
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={12}>
                <ControlledRowTextField
                  label="Description"
                  inputName="description"
                  inputType="textarea"
                  placeholder="Describe this option for customers..."
                  control={control}
                  rules={{
                    required: "Description is required",
                    minLength: {
                      value: 5,
                      message: "Description must be at least 5 characters",
                    },
                    maxLength: {
                      value: 200,
                      message: "Description must be less than 200 characters",
                    },
                  }}
                />
                {errors.description && (
                  <span className="text-danger small">
                    {errors.description.message}
                  </span>
                )}
              </Col>
            </Row>

            <div className="mt-4">
              <div className="alert alert-info">
                <h6 className="mb-2">Value Information:</h6>
                <Row className="small">
                  <Col md={6}>
                    <ul className="mb-0">
                      <li>
                        <strong>Created:</strong>{" "}
                        {dimensionValue?.createdAt
                          ? new Date(
                              dimensionValue.createdAt._seconds * 1000
                            ).toLocaleDateString()
                          : "Unknown"}
                      </li>
                      <li>
                        <strong>Value ID:</strong> {dimensionValue?.id}
                      </li>
                      <li>
                        <strong>Current Order:</strong>{" "}
                        {dimensionValue?.displayOrder}
                      </li>
                      <li>
                        <strong>Image Available:</strong>{" "}
                        {dimensionValue?.imageUrl ? "Yes" : "No"}
                      </li>
                    </ul>
                  </Col>
                  <Col md={6}>
                    <ul className="mb-0">
                      <li>
                        <strong>Dimension:</strong>{" "}
                        {dimension?.displayName || "Loading..."}
                      </li>
                      <li>
                        <strong>Menu Type:</strong> {menuType}
                      </li>
                      <li>
                        <strong>Required:</strong>{" "}
                        {dimension?.isRequired ? "Yes" : "No"}
                      </li>
                    </ul>
                  </Col>
                </Row>
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button
                variant="secondary"
                onClick={() =>
                  navigate(
                    `/productCatalogue/product-groups/${menuType}/dimensions`
                  )
                }
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <ButtonComponent
                type="submit"
                isDisabled={
                  isSubmitting || !displayNameWatcher || !descriptionWatcher
                }
                titel={isSubmitting ? "Updating..." : "Update Value"}
                icon={
                  <FontAwesomeIcon
                    icon={isSubmitting ? faSpinner : faSave}
                    spin={isSubmitting}
                  />
                }
              />
            </div>
          </Form>
        </CardBody>
      </Card>
    </>
  );
};

export default EditDimensionValue;
